import { memo, useRef, useState, type ComponentType, type MouseEvent, type PointerEvent, type ReactNode, type SVGProps } from "react";
import { Link, useNavigate } from "react-router";
import type { CommunitySummary, FeedItem, Post, PostBody, PostVisibility, SharedMessage, UserSummary } from "@atrium/shared";
import { cn } from "../../lib/cn";
import { compact, fullDate, timeAgo } from "../../lib/format";
import { useMyCommunities } from "../../lib/hooks";
import { usePrefs } from "../../lib/prefs";
import { useUI } from "../../lib/uiStore";
import { Avatar, CommunityIcon, UserAvatar } from "../../components/ui/Avatar";
import { Skeleton } from "../../components/ui/Skeleton";
import { Tooltip } from "../../components/ui/Tooltip";
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from "../../components/ui/Menu";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "../../components/ui/ContextMenu";
import { ContextTargetItems, readContextTarget, type ContextTarget } from "../../app/PageContextMenu";
import { RichText } from "../../components/RichText";
import { MediaGrid, ChatMedia } from "../../components/Media";
import { UserBadges } from "../../components/UserBadges";
import {
  IconAnnouncement,
  IconBookmark,
  IconBookmarkFilled,
  IconComment,
  IconEdit,
  IconHash,
  IconLink,
  IconMore,
  IconPin,
  IconQuote,
  IconRepost,
  IconShare,
  IconTrash,
  IconUsers,
} from "../../components/icons";
import {
  IconAt,
  IconChart,
  IconChatOff,
  IconCloseFriends,
  IconFlagReport,
  IconFolderAdd,
  IconHide,
  IconMapPoint,
  IconMuted,
  IconNotInterested,
  IconThread,
  IconUserBlock,
} from "../../components/socialIcons";
import { useAuth, useAuthGate } from "../auth/AuthProvider";
import { usePostActions, copyText } from "./usePostActions";
import { toast } from "../../components/ui/Toast";
import { DoubleTapHeart, ReactionButton, RollingCount, haptic, useCelebrate } from "./Reactions";
import { ContentWarningGate, PollView, PostEmbeds, SensitiveMedia, TaggedPeople } from "./PostParts";
import { openPostDialog } from "./PostDialogs";
import { useViewTracking } from "./views";
import { useReportHandler } from "./reportSlot";


function stop(e: MouseEvent) {
  e.stopPropagation();
}

type Tone = "repost" | "neutral";

const actionBase = "inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-sm text-fg-3 transition-colors duration-150 hover:bg-surface-2";
const toneHover: Record<Tone, string> = { repost: "hover:text-repost", neutral: "hover:text-fg" };
const toneActive: Record<Tone, string> = { repost: "text-repost", neutral: "text-fg" };

function Count({ value }: { value: number }) {
  const { showCounts } = usePrefs();
  if (!showCounts || value <= 0) return null;
  return <RollingCount value={value} />;
}

function ActionButton({
  icon: Icon,
  label,
  count,
  active,
  tone,
  onClick,
  disabled,
  iconClass,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  count?: number;
  active?: boolean;
  tone: Tone;
  onClick?: (e: MouseEvent) => void;
  disabled?: boolean;
  iconClass?: string;
}) {
  return (
    <Tooltip label={label}>
      <button
        type="button"
        aria-label={count && count > 0 ? `${label}, ${count}` : label}
        aria-pressed={active}
        aria-disabled={disabled || undefined}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
        className={cn(actionBase, !disabled && toneHover[tone], active && toneActive[tone], disabled && "opacity-50")}
      >
        <Icon className={cn("icon size-[18px]", iconClass)} aria-hidden />
        {count !== undefined && <Count value={count} />}
      </button>
    </Tooltip>
  );
}

function Time({ at, className }: { at: string; className?: string }) {
  return (
    <Tooltip label={fullDate(at)}>
      <time dateTime={at} className={cn("shrink-0 text-xs text-fg-3 tabular", className)}>
        {timeAgo(at)}
      </time>
    </Tooltip>
  );
}

const AUDIENCE: Record<Exclude<PostVisibility, "public">, { label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }> = {
  followers: { label: "Followers only", icon: IconUsers },
  close_friends: { label: "Close friends", icon: IconCloseFriends },
  mentioned: { label: "Mentioned people only", icon: IconAt },
};

/** Audience, edited and location markers in the header. */
function Meta({ post }: { post: Post }) {
  const a = post.visibility !== "public" ? AUDIENCE[post.visibility] : null;
  return (
    <>
      {a && (
        <Tooltip label={a.label}>
          <a.icon className="icon size-3.5 shrink-0 text-fg-3" aria-label={a.label} />
        </Tooltip>
      )}
      {post.editedAt && (
        <Tooltip label={`Edited ${fullDate(post.editedAt)}`}>
          <span className="shrink-0 text-xs text-fg-3">Edited</span>
        </Tooltip>
      )}
    </>
  );
}

/** Small rounded-square community mark used in post context lines. */
function CommunityMark({ c, className }: { c: Pick<CommunitySummary, "iconUrl" | "name">; className?: string }) {
  return <CommunityIcon src={c.iconUrl} name={c.name} size="2xs" className={className} />;
}

/** Compact author line for embeds: avatar, name, time. */
function EmbedHeader({ user, createdAt, community }: { user: UserSummary; createdAt: string; community?: CommunitySummary | null }) {
  return (
    <div className="flex min-w-0 items-center gap-2 text-sm">
      {community ? <CommunityMark c={community} className="!size-5" /> : <UserAvatar user={user} size="xs" plain />}
      <span className="truncate font-semibold text-fg">{community ? community.name : user.displayName}</span>
      {!community && <UserBadges user={user} />}
      {community && <span className="truncate text-xs text-fg-3">by {user.displayName}</span>}
      <Time at={createdAt} className="ml-auto" />
    </div>
  );
}

export function SharedMessageEmbed({ msg }: { msg: SharedMessage }) {
  const { data: mine } = useMyCommunities();
  const isMember = mine?.some((c) => c.id === msg.community.id);
  const href = isMember ? `/c/${msg.community.id}/${msg.channel.id}` : `/community/${msg.community.slug}`;
  const navigate = useNavigate();
  // A clickable container rather than <a>, because the message itself may contain links.
  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`Message in #${msg.channel.name}, ${msg.community.name}`}
      onClick={(e) => {
        e.stopPropagation();
        navigate(href);
      }}
      onKeyDown={(e) => e.key === "Enter" && navigate(href)}
      className="mt-3 block cursor-pointer rounded-lg border border-line bg-bg-subtle transition-colors hover:border-line-strong"
    >
      <div className="flex h-9 items-center gap-1.5 border-b border-line px-3 text-xs text-fg-3">
        <CommunityMark c={msg.community} />
        <span className="font-medium text-fg-2">{msg.community.name}</span>
        <IconHash className="icon size-3.5" aria-hidden />
        <span className="truncate">{msg.channel.name}</span>
      </div>
      <div className="flex gap-2.5 px-3 py-2.5">
        <Avatar src={msg.author.avatarUrl} staticSrc={msg.author.avatarStaticUrl} name={msg.author.displayName} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 text-sm">
            <span className="font-semibold text-fg">{msg.author.displayName}</span>
            <span className="text-xs text-fg-3 tabular">{timeAgo(msg.createdAt)}</span>
          </div>
          {msg.content && <RichText text={msg.content} className="text-base leading-[1.4rem] text-fg line-clamp-[12]" variant="chat" />}
          <ChatMedia media={msg.media} />
        </div>
      </div>
    </div>
  );
}

export function QuoteEmbed({ post }: { post: PostBody }) {
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState(false);
  const body = (
    <>
      {post.content && <RichText text={post.content} className="mt-1.5 text-base leading-[1.4rem] text-fg line-clamp-6" />}
      {post.media.length > 0 && (
        <SensitiveMedia sensitive={post.sensitive && !post.contentWarning}>
          <MediaGrid media={post.media.slice(0, 4)} className="mt-2" />
        </SensitiveMedia>
      )}
      {post.sharedMessage && <SharedMessageEmbed msg={post.sharedMessage} />}
    </>
  );
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/p/${post.id}`);
      }}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/p/${post.id}`)}
      className="mt-3 cursor-pointer rounded-lg border border-line px-3 py-2.5 transition-colors hover:border-line-strong hover:bg-surface/40"
    >
      <EmbedHeader user={post.author} createdAt={post.createdAt} community={post.community} />
      {post.contentWarning ? (
        <ContentWarningGate warning={post.contentWarning} revealed={revealed} onReveal={setRevealed}>
          {body}
        </ContentWarningGate>
      ) : (
        body
      )}
    </div>
  );
}

/** Loading placeholder matching the post card layout. */
export function PostCardSkeleton() {
  return (
    <div className="border-b border-line px-4 py-4">
      <div className="max-w-[720px]">
        <div className="flex items-center gap-2.5">
          <Skeleton className="size-7 rounded-full" />
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="ml-auto h-3 w-8" />
        </div>
        <div className="mt-3 space-y-2">
          <Skeleton className="h-3.5 w-full" />
          <Skeleton className="h-3.5 w-3/4" />
        </div>
        <div className="mt-4 flex gap-4">
          <Skeleton className="h-3.5 w-10" />
          <Skeleton className="h-3.5 w-10" />
          <Skeleton className="h-3.5 w-10" />
        </div>
      </div>
    </div>
  );
}

const DOUBLE_TAP_MS = 280;

/**
 * A post. Layout: optional context line (community, repost, pin, reply), a
 * compact author header, the body at full width, then a quiet grouped action
 * bar. Double-click (or double-tap) the body to like it.
 */
export const PostCard = memo(function PostCard({
  item,
  post: postProp,
  detail,
  source = "feed",
}: {
  item?: FeedItem;
  post?: Post;
  detail?: boolean;
  /** Where the post is shown, for view analytics. */
  source?: "feed" | "profile" | "post" | "search" | "community" | "saved";
}) {
  const post = (item?.post ?? postProp)!;
  const repostedBy = item?.repostedBy ?? null;
  const navigate = useNavigate();
  const { me } = useAuth();
  const gate = useAuthGate();
  const actions = usePostActions();
  const openComposer = useUI((s) => s.openComposer);
  const prefs = usePrefs();
  const celebrate = useCelebrate();
  const dense = prefs.density === "compact" && !detail;
  const own = me?.id === post.author.id;
  const [revealed, setRevealed] = useState(false);
  const [heart, setHeart] = useState(0);
  const [spin, setSpin] = useState(false);
  const tap = useRef<{ t: number; timer: ReturnType<typeof setTimeout> | null }>({ t: 0, timer: null });
  const viewRef = useViewTracking<HTMLElement>(post.id, source, !!me && !own);

  const open = () => {
    if (window.getSelection()?.toString()) return;
    if (!detail) navigate(`/p/${post.id}`);
  };

  const doubleLike = () => {
    if (!me) return;
    if (celebrate.reactions) setHeart((h) => h + 1);
    haptic();
    if (!post.liked) void actions.react(post, "like");
  };

  /** Body clicks wait briefly for a second tap; a double tap likes instead of opening. */
  const onBodyPointerUp = (e: PointerEvent) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest("a,button,[role=link],video,input")) return;
    const now = performance.now();
    if (now - tap.current.t < DOUBLE_TAP_MS) {
      if (tap.current.timer) clearTimeout(tap.current.timer);
      tap.current = { t: 0, timer: null };
      doubleLike();
      return;
    }
    tap.current.t = now;
    if (!detail) tap.current.timer = setTimeout(() => open(), DOUBLE_TAP_MS);
  };

  // A post published by a community is headed by the community itself; a post
  // that links to or shares from a community shows that community as context.
  const announcement = post.community;
  const place = announcement ? null : (post.linkedCommunities[0] ?? post.sharedMessage?.community ?? null);
  const hasContext = !!place || !!repostedBy || post.pinned || !!post.replyTo;

  const header = announcement ? (
    <div className="flex min-w-0 flex-1 items-center gap-2.5">
      <Link to={`/community/${announcement.slug}`} onClick={stop} className="shrink-0" aria-label={announcement.name}>
        <CommunityIcon src={announcement.iconUrl} name={announcement.name} size={detail ? "sm" : "xs"} className={detail ? undefined : "!size-7 !rounded-[8px]"} />
      </Link>
      <div className="flex min-w-0 flex-1 items-center gap-1.5 text-sm">
        <Link to={`/community/${announcement.slug}`} onClick={stop} className="truncate font-semibold text-fg hover:underline">
          {announcement.name}
        </Link>
        <Tooltip label="Community announcement">
          <IconAnnouncement className="icon size-4 shrink-0 text-fg-3" aria-label="Announcement" />
        </Tooltip>
        <Link to={`/u/${post.author.username}`} onClick={stop} className="truncate text-xs text-fg-3 hover:text-fg-2">
          by {post.author.displayName}
        </Link>
      </div>
    </div>
  ) : (
    <div className="flex min-w-0 flex-1 items-center gap-2.5">
      <Link to={`/u/${post.author.username}`} onClick={stop} className="shrink-0" aria-label={post.author.displayName}>
        <UserAvatar user={post.author} size={detail ? "md" : "sm"} />
      </Link>
      <div className={cn("min-w-0 flex-1", !detail && "flex items-center gap-1.5")}>
        <div className="flex min-w-0 items-center gap-1.5">
          <Link to={`/u/${post.author.username}`} onClick={stop} className="block truncate text-sm font-semibold text-fg hover:underline">
            {post.author.displayName}
          </Link>
          <UserBadges user={post.author} />
          {detail && <Meta post={post} />}
        </div>
        {detail && <div className="truncate text-xs text-fg-3">@{post.author.username}</div>}
        {!detail && <Meta post={post} />}
      </div>
    </div>
  );

  const body = (
    <>
      {post.content && (
        <RichText
          text={post.content}
          me={me?.username}
          className={cn("max-w-[68ch] text-fg", detail ? "mt-3 text-lg leading-7" : cn("text-base leading-[1.45rem]", dense ? "mt-1.5" : "mt-2"))}
        />
      )}
      {post.media.length > 0 && (
        <SensitiveMedia sensitive={post.sensitive && !post.contentWarning}>
          <MediaGrid media={post.media} className={dense ? "mt-2" : "mt-3"} />
          <TaggedPeople media={post.media} />
        </SensitiveMedia>
      )}
      <PostEmbeds embeds={post.embeds} />
      {post.poll && <PollView post={post} own={own} />}
      {post.sharedMessage && <SharedMessageEmbed msg={post.sharedMessage} />}
      {post.quote && <QuoteEmbed post={post.quote} />}
      {post.quoteUnavailable && <div className="mt-3 rounded-lg border border-line px-3 py-3 text-sm text-fg-3">This post is no longer available.</div>}
    </>
  );

  return (
    <PostContextMenu post={post} own={own} detail={detail}>
      <article
        ref={viewRef}
        onClick={(e) => {
          // Clicks on the body are handled by the double-tap logic.
          if ((e.target as HTMLElement).closest("[data-post-body]")) return;
          open();
        }}
        className={cn("border-b border-line px-4 transition-colors", dense ? "pb-1 pt-2.5" : "pb-1.5 pt-3.5", !detail && "cursor-pointer hover:bg-surface/40")}
        aria-label={`Post by ${post.author.displayName}`}
      >
        <div className="max-w-[720px]">
          {hasContext && (
            <div className={cn("flex min-w-0 items-center gap-1.5 text-xs text-fg-3", dense ? "mb-1.5" : "mb-2")}>
              {place && (
                <Link
                  to={`/community/${place.slug}`}
                  onClick={stop}
                  className="flex min-w-0 items-center gap-1.5 rounded font-medium text-fg-2 hover:text-fg"
                  aria-label={`In ${place.name}`}
                >
                  <CommunityMark c={place} />
                  <span className="truncate">{place.name}</span>
                </Link>
              )}
              {place && (repostedBy || post.pinned) && <span aria-hidden>·</span>}
              {repostedBy ? (
                <span className="flex min-w-0 items-center gap-1">
                  <IconRepost className="icon size-3.5 shrink-0" aria-hidden />
                  <Link to={`/u/${repostedBy.username}`} onClick={stop} className="truncate hover:text-fg-2">
                    {repostedBy.id === me?.id ? "You" : repostedBy.displayName} reposted
                  </Link>
                </span>
              ) : post.pinned ? (
                <span className="flex items-center gap-1">
                  <IconPin className="icon size-3.5" aria-hidden /> Pinned
                </span>
              ) : null}
              {post.replyTo && !repostedBy && post.replyTo.author.id !== post.author.id && (
                <span className="flex min-w-0 items-center gap-1">
                  {(place || post.pinned) && <span aria-hidden>·</span>}
                  Replying to
                  <Link to={`/p/${post.replyTo.id}`} onClick={stop} className="truncate font-medium text-fg-2 hover:text-fg">
                    @{post.replyTo.author.username}
                  </Link>
                </span>
              )}
            </div>
          )}

          <header className="flex items-center gap-3">
            {header}
            {!detail && <Time at={post.createdAt} />}
          </header>

          <div data-post-body className="relative" onPointerUp={onBodyPointerUp} onDoubleClick={(e) => e.preventDefault()}>
            {post.contentWarning ? (
              <ContentWarningGate warning={post.contentWarning} revealed={revealed} onReveal={setRevealed}>
                {body}
              </ContentWarningGate>
            ) : (
              body
            )}
            <DoubleTapHeart seed={heart} />
          </div>

          {(post.location || (post.threadCount > 0 && !detail)) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-3">
              {post.location && (
                <span className="flex items-center gap-1">
                  <IconMapPoint className="icon size-3.5" aria-hidden />
                  {post.location}
                </span>
              )}
              {post.threadCount > 0 && !detail && (
                <Link to={`/p/${post.id}`} onClick={stop} className="flex items-center gap-1 font-medium text-accent hover:underline">
                  <IconThread className="icon size-3.5" aria-hidden />
                  Show thread · {post.threadCount + 1} posts
                </Link>
              )}
            </div>
          )}
          {detail && (
            <div className="mt-3 flex flex-wrap items-center gap-x-2 text-sm text-fg-3 tabular">
              <time dateTime={post.createdAt}>{fullDate(post.createdAt)}</time>
              {post.viewCount > 0 && prefs.showCounts && (
                <>
                  <span aria-hidden>·</span>
                  <span>
                    {compact(post.viewCount)} {post.viewCount === 1 ? "view" : "views"}
                  </span>
                </>
              )}
              {post.replyPolicy !== "everyone" && (
                <>
                  <span aria-hidden>·</span>
                  <span className="flex items-center gap-1">
                    <IconChatOff className="icon size-3.5" aria-hidden />
                    {post.replyPolicy === "none" ? "Replies off" : post.replyPolicy === "following" ? "People the author follows can reply" : "Mentioned people can reply"}
                  </span>
                </>
              )}
            </div>
          )}

          <div className={cn("flex items-center", detail ? "mt-2 border-t border-line pt-1.5" : dense ? "mt-1" : "mt-2")}>
            <div className="-ml-2 flex items-center gap-0.5">
              <ReactionButton post={post} showCount={prefs.showCounts} onReact={gate((kind) => void actions.react(post, kind))} />
              <ActionButton
                icon={IconComment}
                label={post.canReply || !me ? "Reply" : "Replies are limited"}
                count={post.commentCount}
                tone="neutral"
                disabled={!!me && !post.canReply}
                onClick={() => navigate(`/p/${post.id}${detail ? "" : "#reply"}`)}
              />
              <Menu>
                <Tooltip label="Repost or quote">
                  <MenuTrigger asChild>
                    <button
                      type="button"
                      aria-label={post.reposted ? "Reposted, open repost options" : "Repost or quote"}
                      onClick={stop}
                      className={cn(actionBase, toneHover.repost, post.reposted && toneActive.repost)}
                    >
                      <IconRepost className={cn("icon size-[18px]", spin && "repost-spin")} aria-hidden onAnimationEnd={() => setSpin(false)} />
                      <Count value={post.repostCount} />
                    </button>
                  </MenuTrigger>
                </Tooltip>
                <MenuContent align="start">
                  <MenuItem
                    icon={IconRepost}
                    disabled={post.visibility !== "public"}
                    onSelect={gate(() => {
                      if (!post.reposted && celebrate.reactions) setSpin(true);
                      void actions.repost(post);
                    })}
                  >
                    {post.reposted ? "Undo repost" : "Repost"}
                  </MenuItem>
                  <MenuItem icon={IconQuote} disabled={post.visibility !== "public"} onSelect={gate(() => openComposer({ quoteId: post.id }))}>
                    Quote
                  </MenuItem>
                </MenuContent>
              </Menu>
            </div>
            <div className="-mr-2 ml-auto flex items-center gap-0.5">
              <ActionButton
                icon={post.bookmarked ? IconBookmarkFilled : IconBookmark}
                label={post.bookmarked ? "Remove from saved" : "Save"}
                active={post.bookmarked}
                tone="neutral"
                onClick={gate(() => void actions.bookmark(post, { remove: post.bookmarked }))}
              />
              <ActionButton icon={IconShare} label="Share" tone="neutral" onClick={() => void actions.share(post)} />
              <PostMenu post={post} own={own} detail={detail} />
            </div>
          </div>
        </div>
      </article>
    </PostContextMenu>
  );
});

function PostMenu({ post, own, detail }: { post: Post; own: boolean; detail?: boolean }): ReactNode {
  return (
    <Menu>
      <Tooltip label="More">
        <MenuTrigger asChild>
          <button type="button" onClick={stop} aria-label="More options" className={cn(actionBase, toneHover.neutral)}>
            <IconMore className="icon size-[18px]" aria-hidden />
          </button>
        </MenuTrigger>
      </Tooltip>
      <MenuContent align="end">
        <PostMenuItems post={post} own={own} detail={detail} />
      </MenuContent>
    </Menu>
  );
}

/** Right-click / long-press on a post: what's under the pointer (link, image, selection), then the "⋯" menu's items. */
function PostContextMenu({ post, own, detail, children }: { post: Post; own: boolean; detail?: boolean; children: ReactNode }) {
  const [target, setTarget] = useState<ContextTarget>({ selection: "", href: null, image: null });
  return (
    <ContextMenu modal={false}>
      <ContextMenuTrigger asChild onContextMenu={(e) => setTarget(readContextTarget(e))}>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextTargetItems target={target} separatorAfter />
        <PostMenuItems post={post} own={own} detail={detail} context />
      </ContextMenuContent>
    </ContextMenu>
  );
}

/** The post's actions, as dropdown items or (with `context`) context-menu items. */
function PostMenuItems({ post, own, detail, context }: { post: Post; own: boolean; detail?: boolean; context?: boolean }) {
  const actions = usePostActions();
  const navigate = useNavigate();
  const gate = useAuthGate();
  const { me } = useAuth();
  const report = useReportHandler();
  const other = !!me && !own;
  const Item = context ? ContextMenuItem : MenuItem;
  const Separator = context ? ContextMenuSeparator : MenuSeparator;
  return (
    <>
      <Item
        icon={IconLink}
        onSelect={() => {
          void copyText(`${window.location.origin}/p/${post.id}`);
          toast.show("Link copied");
        }}
      >
        Copy link
      </Item>
      {me && (
        <Item icon={IconFolderAdd} onSelect={() => openPostDialog("save", post)}>
          Save to collection
        </Item>
      )}
      {own && (
        <>
          <Item icon={IconEdit} onSelect={() => openPostDialog("edit", post)}>
            Edit post
          </Item>
          {!post.community && (
            <Item icon={IconPin} onSelect={() => void actions.pin(post)}>
              {post.pinned ? "Unpin from profile" : "Pin to profile"}
            </Item>
          )}
          <Item icon={IconChart} onSelect={() => openPostDialog("analytics", post)}>
            View activity
          </Item>
          <Separator />
          <Item icon={IconTrash} danger onSelect={() => void actions.remove(post).then((ok) => ok && detail && navigate(-1))}>
            Delete post
          </Item>
        </>
      )}
      {other && (
        <>
          <Separator />
          {!detail && (
            <>
              <Item icon={IconHide} onSelect={() => void actions.feedback(post, "hide")}>
                Hide post
              </Item>
              <Item icon={IconNotInterested} onSelect={() => void actions.feedback(post, "not_interested")}>
                Not interested
              </Item>
            </>
          )}
          <Item icon={IconMuted} onSelect={gate(() => void actions.muteAuthor(post))}>
            Mute {post.author.displayName}
          </Item>
          <Item icon={IconUserBlock} danger onSelect={gate(() => void actions.blockAuthor(post))}>
            Block {post.author.displayName}
          </Item>
          {report && (
            <Item icon={IconFlagReport} danger onSelect={() => report({ type: "post", post })}>
              Report post
            </Item>
          )}
        </>
      )}
      {post.visibility !== "public" && (
        <>
          <Separator />
          <div className="flex items-center gap-2 px-2.5 py-1.5 text-xs text-fg-3">
            {(() => {
              const A = AUDIENCE[post.visibility].icon;
              return <A className="icon size-3.5" aria-hidden />;
            })()}
            {AUDIENCE[post.visibility].label} · can't be reposted
          </div>
        </>
      )}
    </>
  );
}
